import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { classes } from "@/data/classes";

export type EditMode =
  | { kind: "payment"; stripeSessionId: string }
  | { kind: "interest"; rowId: string };

export interface EditableRegistration {
  classId: string;
  adultName: string;
  email: string;
  childName: string;
  childAge: string | number;
  selectedSessions?: string[]; // payments only
  topics?: string | null; // interest only
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: EditMode | null;
  initial: EditableRegistration | null;
  onSaved: (updated: EditableRegistration) => void;
}

const EditRegistrationDialog = ({ open, onOpenChange, mode, initial, onSaved }: Props) => {
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendingPacket, setResendingPacket] = useState(false);
  const [form, setForm] = useState<EditableRegistration>({
    classId: "",
    adultName: "",
    email: "",
    childName: "",
    childAge: "",
    selectedSessions: [],
    topics: null,
  });

  useEffect(() => {
    if (initial) {
      setForm({
        ...initial,
        selectedSessions: initial.selectedSessions ?? [],
        topics: initial.topics ?? null,
      });
    }
  }, [initial]);

  if (!mode || !initial) return null;

  const cls = classes.find((c) => c.id === form.classId);
  const sessions = cls?.sessions ?? [];
  const hasSessions = mode.kind === "payment" && sessions.length > 0;

  const toggleSession = (label: string) => {
    setForm((prev) => {
      const current = prev.selectedSessions ?? [];
      return {
        ...prev,
        selectedSessions: current.includes(label)
          ? current.filter((s) => s !== label)
          : [...current, label],
      };
    });
  };

  const handleSave = async () => {
    const age = parseInt(String(form.childAge), 10);
    if (!form.adultName.trim() || !form.email.trim() || !form.childName.trim() || isNaN(age) || age < 1 || age > 18) {
      toast({ title: "Invalid input", description: "Please fill all fields with valid values.", variant: "destructive" });
      return;
    }

    setSaving(true);
    try {
      if (mode.kind === "payment") {
        const payload = {
          class_id: form.classId,
          adult_name: form.adultName.trim(),
          email: form.email.trim(),
          child_name: form.childName.trim(),
          child_age: age,
          selected_sessions: form.selectedSessions ?? [],
          payment_status: "complete",
        };

        // Update existing row by stripe_session_id (avoids partial-unique-index upsert issues)
        const { data: updatedRows, error: updateError } = await supabase
          .from("class_payments")
          .update(payload)
          .eq("stripe_session_id", mode.stripeSessionId)
          .select("id");
        if (updateError) throw updateError;

        // If no row exists yet (Stripe-only payment), insert one
        if (!updatedRows || updatedRows.length === 0) {
          const { error: insertError } = await supabase
            .from("class_payments")
            .insert({
              ...payload,
              stripe_session_id: mode.stripeSessionId,
              amount_paid: 0,
            });
          if (insertError) throw insertError;
        }
      } else {
        const { error } = await supabase
          .from("class_interest")
          .update({
            class_id: form.classId,
            adult_name: form.adultName.trim(),
            email: form.email.trim(),
            child_name: form.childName.trim(),
            child_age: age,
            topics: form.topics?.trim() || null,
          })
          .eq("id", mode.rowId);
        if (error) throw error;
      }

      onSaved({ ...form, childAge: age });
      toast({ title: "Saved" });
      onOpenChange(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unknown error";
      console.error("Edit save failed:", err);
      toast({ title: "Could not save", description: message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Edit Registration</DialogTitle>
          <DialogDescription>
            {cls?.title ?? form.classId}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="edit_class">Class</Label>
            <Select
              value={form.classId}
              onValueChange={(value) =>
                setForm((prev) => ({ ...prev, classId: value, selectedSessions: [] }))
              }
            >
              <SelectTrigger id="edit_class">
                <SelectValue placeholder="Select a class" />
              </SelectTrigger>
              <SelectContent>
                {classes.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="edit_adult">Parent / Guardian</Label>
              <Input id="edit_adult" value={form.adultName} onChange={(e) => setForm({ ...form, adultName: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="edit_email">Email</Label>
              <Input id="edit_email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="edit_child">Child's Name</Label>
              <Input id="edit_child" value={form.childName} onChange={(e) => setForm({ ...form, childName: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="edit_age">Child's Age</Label>
              <Input id="edit_age" type="number" min={1} max={18} value={String(form.childAge)} onChange={(e) => setForm({ ...form, childAge: e.target.value })} />
            </div>
          </div>

          {mode.kind === "interest" && (
            <div className="space-y-1.5">
              <Label htmlFor="edit_topics">Topics of Interest</Label>
              <Textarea
                id="edit_topics"
                value={form.topics ?? ""}
                onChange={(e) => setForm({ ...form, topics: e.target.value || null })}
                rows={3}
                placeholder="e.g. Automotive, Physical Therapy, Journalism..."
              />
            </div>
          )}

          {hasSessions && (
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Sessions</Label>
              {sessions.map((s) => (
                <label key={s.label} className="flex items-center gap-3 rounded-md border border-border bg-background p-2 cursor-pointer hover:bg-accent/50">
                  <Checkbox
                    checked={(form.selectedSessions ?? []).includes(s.label)}
                    onCheckedChange={() => toggleSession(s.label)}
                  />
                  <span className="text-sm flex-1"><strong>{s.label}</strong> — {s.dates}</span>
                </label>
              ))}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:justify-between">
          {mode.kind === "payment" ? (
            <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="secondary"
              disabled={saving || resending}
              onClick={async () => {
                if (mode.kind !== "payment") return;
                setResending(true);
                try {
                  const { data, error } = await supabase.functions.invoke(
                    "admin-resend-class-confirmation",
                    { body: { stripeSessionId: mode.stripeSessionId } },
                  );
                  if (error || (data as any)?.error) {
                    toast({
                      title: "Couldn't resend",
                      description: (data as any)?.error ?? error?.message ?? "Unknown error",
                      variant: "destructive",
                    });
                  } else {
                    toast({ title: "Confirmation email resent" });
                  }
                } finally {
                  setResending(false);
                }
              }}
            >
              {resending ? "Resending…" : "Resend confirmation email"}
            </Button>
            <Button
              type="button"
              variant="secondary"
              disabled={saving || resendingPacket}
              onClick={async () => {
                if (mode.kind !== "payment") return;
                setResendingPacket(true);
                try {
                  const { data, error } = await supabase.functions.invoke(
                    "welcome-packet-admin",
                    { body: { action: "resend", stripeSessionId: mode.stripeSessionId } },
                  );
                  if (error || (data as any)?.error) {
                    toast({
                      title: "Couldn't resend welcome packet",
                      description: (data as any)?.error ?? error?.message ?? "Unknown error",
                      variant: "destructive",
                    });
                  } else {
                    toast({ title: "Welcome packet resent" });
                  }
                } finally {
                  setResendingPacket(false);
                }
              }}
            >
              {resendingPacket ? "Sending…" : "Resend welcome packet"}
            </Button>
            </div>
          ) : <span />}

          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving}>{saving ? "Saving…" : "Save changes"}</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditRegistrationDialog;
